import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Paper,
    Button,
    Tabs,
    Tab,
    CircularProgress,
    Chip,
    Divider,
    Alert,
    Snackbar,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useNavigate } from "react-router";
import { AiActions } from "../../Services/_actions/index.js";

const userInfo = localStorage?.getItem('userInfo');
const CampanyID = userInfo ? JSON.parse(userInfo)?.profile?.organisation?.organisation_id : null;
const ORGANIZATION_ID = CampanyID; // replace with dynamic value if available from auth/context
const instructionTypes = ["Chat", "Image", "Video", "Research", "Presentation"];

// Maps tab label → the key used in the API payload
const typeToApiKey: Record<string, string> = {
    Chat: "chat",
    Image: "image",
    Video: "video",
    Research: "research",
    Presentation: "presentation",
};

type FormData = Record<string, string>;

// ── Component ────────────────────────────────────────────────────────────────

export default function InstructionSetForm() {
    const navigate = useNavigate();
    const action = AiActions();

    const [activeTab, setActiveTab] = useState(0);
    const [formData, setFormData] = useState<FormData>({
        Chat: "",
        Image: "",
        Video: "",
        Research: "",
        Presentation: "",
    });

    // Tracks whether a record already exists (PUT) or needs to be created (POST)
    const [recordExists, setRecordExists] = useState<boolean | null>(null);
    const [isFetching, setIsFetching] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [savedTabs, setSavedTabs] = useState<Set<string>>(new Set());

    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error";
    }>({ open: false, message: "", severity: "success" });

    // ── Fetch existing instructions on mount ─────────────────────────────────
    useEffect(() => {
        const fetchInstructions = async () => {
            setIsFetching(true);
            try {
                const res = await action.getOrganizationInstruction(ORGANIZATION_ID);

                const record = Array.isArray(res) ? res[0] : res;
                if (record && !record?.general_error) {
                    setRecordExists(true);
                    const instructionSet = record?.instruction_set ?? {};

                    // ✅ Build from scratch — don't spread stale formData
                    const mapped: FormData = {
                        Chat: "",
                        Image: "",
                        Video: "",
                        Research: "",
                        Presentation: "",
                    };

                    instructionTypes.forEach((type) => {
                        const apiKey = `${typeToApiKey[type]}_instruction`;
                        if (apiKey in instructionSet) {
                            mapped[type] = instructionSet[apiKey] ?? "";
                        }
                    });

                    setFormData(mapped);
                } else {
                    setRecordExists(false);
                }
            } catch (err) {
                console.error("Fetch error:", err); // ❌ you had empty catch — this hid real errors!
                setRecordExists(false);
            } finally {
                setIsFetching(false);
            }
        };

        fetchInstructions();
    }, []); // ✅ keep empty — but now mapped is not stale
    // ── Handlers ─────────────────────────────────────────────────────────────

    const handleChange = (type: string, value: string) => {
        setFormData((prev) => ({ ...prev, [type]: value }));
        // Clear saved indicator when user edits
        setSavedTabs((prev) => {
            const next = new Set(prev);
            next.delete(type);
            return next;
        });
    };
    const handleSave = async () => {
        setIsSaving(true);

        const activeType = instructionTypes[activeTab];

        try {
            const instructionSet: Record<string, string> = {};

            instructionTypes.forEach((type) => {
                instructionSet[`${type.toLowerCase()}_instruction`] = formData[type];
            });

            const payload = {
                organization_id: ORGANIZATION_ID,
                instruction_set: instructionSet,
            };

            if (recordExists) {
                await action.updateOrganizationInstruction(payload);
            } else {
                await action.createOrganizationInstruction(payload);
                setRecordExists(true);
            }

            setSavedTabs((prev) => new Set(prev).add(activeType));

            setSnackbar({
                open: true,
                message: "Instruction saved successfully!",
                severity: "success",
            });

        } catch (err) {
            console.error("Failed to save instruction:", err);

            setSnackbar({
                open: true,
                message: "Failed to save. Please try again.",
                severity: "error",
            });

        } finally {
            setIsSaving(false);
        }
    };

    const handleBack = () => {
        navigate("/r/machadalostudio/ai");
    };

    // ── Render ────────────────────────────────────────────────────────────────

    const activeType = instructionTypes[activeTab];

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 3,
                border: "1px solid #ebebf0",
                mx: "auto",
                mt: 2,
            }}
        >
            {/* ── Header ── */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
                <Box>
                    <Typography variant="h6" fontWeight={700} color="#1a1a2e">
                        Instruction Set
                    </Typography>
                    <Typography variant="body2" color="#9e9e9e" sx={{ mt: 0.3 }}>
                        Configure AI behaviour for each mode separately.
                    </Typography>
                </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {isFetching ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress size={32} />
                </Box>
            ) : (
                <>
                    {/* ── Tabs ── */}
                    <Tabs
                        value={activeTab}
                        onChange={(_e, newValue) => setActiveTab(newValue)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            mb: 2.5,
                            "& .MuiTab-root": { textTransform: "none", fontSize: 13, fontWeight: 500, minHeight: 38 },
                            "& .Mui-selected": { fontWeight: 700 },
                        }}
                    >
                        {instructionTypes.map((type) => (
                            <Tab
                                key={type}
                                label={
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                                        {type}
                                        {savedTabs.has(type) && (
                                            <CheckCircleOutlineIcon sx={{ fontSize: 14, color: "success.main" }} />
                                        )}
                                    </Box>
                                }
                            />
                        ))}
                    </Tabs>

                    {/* ── Active Instruction Input ── */}
                    <TextField
                        fullWidth
                        multiline
                        minRows={8}
                        label={`${activeType} Instruction`}
                        placeholder={`Enter system instructions for the ${activeType} mode…\n\nExample:\n- Tone and persona\n- Restrictions\n- Output format guidelines`}
                        value={formData[activeType]}
                        onChange={(e) => handleChange(activeType, e.target.value)}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "10px",
                                fontSize: 13.5,
                                lineHeight: 1.7,
                            },
                            "& .MuiOutlinedInput-root textarea": {
                                height:'250px !important',
                                overflowY: 'auto',
                            },

                        }}
                    />

                    <Typography
                        variant="caption"
                        color="#b0b0b0"
                        sx={{ mt: 0.8, display: "block" }}
                        overflow='auto'
                        height='250px'
                    >
                        {formData[activeType].length} character{formData[activeType].length !== 1 ? "s" : ""}
                    </Typography>

                    {/* ── Actions ── */}
                    <Box mt={3} display="flex" justifyContent="space-between" alignItems="center">
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBack}
                            sx={{ textTransform: "none", borderRadius: "8px", fontSize: 13 }}
                        >
                            Back
                        </Button>

                        <Button
                            variant="contained"
                            size="small"
                            startIcon={isSaving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon />}
                            onClick={handleSave}
                            disabled={isSaving || !formData[activeType].trim()}
                            sx={{
                                textTransform: "none",
                                borderRadius: "8px",
                                fontSize: 13,
                                boxShadow: "none",
                                px: 2.5,
                            }}
                        >
                            {isSaving ? "Saving…" : recordExists ? "Update Instruction" : "Save Instruction"}
                        </Button>
                    </Box>
                </>
            )}

            {/* ── Snackbar ── */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3500}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    sx={{ borderRadius: "10px", fontSize: 13 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
}